/**
 * LIVE ACTIVITY BAR
 *
 * Claude Code-style status line shown while a session is running:
 *
 *   [breathing provider logo]  Reading index.ts…   12s · 3.4k tokens
 *
 * The label follows the agent in real time: current running tool →
 * "Writing response" while text streams → "Thinking" otherwise.
 * Reused by both the My Agent view (AiChatMessages) and the project chat
 * (ChatConversation).
 */

import { useMemo } from "react";
import { useReducedMotion } from "framer-motion";
import { AnimatedProviderLogo } from "./AnimatedProviderLogo";
import { deriveLiveActivity, estimateLiveTokens, formatTokens } from "./liveActivity";
import { useElapsedSeconds } from "@/hooks/useElapsedSeconds";
import { cn } from "@/lib/utils";
import type { AiChatSession } from "@/types/ai-chat";

function AnimatedEllipsis() {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return (
      <span className="inline-flex" aria-hidden="true">
        <span>.</span>
        <span>.</span>
        <span>.</span>
      </span>
    );
  }

  return (
    <span className="inline-flex" aria-hidden="true">
      <span className="animate-[pulse_1.4s_ease-in-out_infinite]">.</span>
      <span className="animate-[pulse_1.4s_ease-in-out_0.2s_infinite]">.</span>
      <span className="animate-[pulse_1.4s_ease-in-out_0.4s_infinite]">.</span>
    </span>
  );
}

interface Props {
  session: AiChatSession;
  className?: string;
}

export function LiveActivityBar({ session, className }: Props) {
  const active = session.state === "running";
  const activity = useMemo(() => deriveLiveActivity(session), [session]);
  const elapsed = useElapsedSeconds(session.runStartedAt, active);
  const tokens = estimateLiveTokens(session);

  if (!active) return null;

  return (
    <div
      className={cn("flex items-center gap-2 text-xs text-muted-foreground min-w-0", className)}
      data-testid="live-activity-bar"
    >
      <AnimatedProviderLogo provider={session.provider} size={16} active />
      <span className="truncate min-w-0">
        <span className={activity.kind === "tool" ? "font-mono text-[11px]" : ""}>
          {activity.label}
        </span>
        <AnimatedEllipsis />
      </span>
      <span className="flex-shrink-0 tabular-nums text-muted-foreground/70">
        {elapsed}s{tokens > 0 && <> · {formatTokens(tokens)} tokens</>}
      </span>
    </div>
  );
}
